import React, { useState, useEffect } from 'react';
import { Heart, Music, Quote, Share2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT_PALETTES = [
  { bg: 'from-[#D4AF37]/15 to-[#D4AF37]/5', border: 'border-[#D4AF37]/20', icon: 'text-[#D4AF37]' },
  { bg: 'from-[#991B1B]/15 to-[#991B1B]/5', border: 'border-[#991B1B]/20', icon: 'text-red-400' },
  { bg: 'from-[#1E3A8A]/15 to-[#1E3A8A]/5', border: 'border-[#1E3A8A]/20', icon: 'text-blue-400' },
  { bg: 'from-emerald-600/15 to-emerald-600/5', border: 'border-emerald-600/20', icon: 'text-emerald-400' },
  { bg: 'from-purple-600/15 to-purple-600/5', border: 'border-purple-600/20', icon: 'text-purple-400' },
  { bg: 'from-amber-500/15 to-amber-500/5', border: 'border-amber-500/20', icon: 'text-amber-400' },
];

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function DedicationWallPage() {
  const [dedications, setDedications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dedications?limit=50`).then(r => {
      setDedications(r.data.dedications || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleShare = (ded) => {
    const shareUrl = `${API}/share/dedication/${ded.id}`;
    const text = `"${ded.message}" — ${ded.listener_name} requested "${ded.song_name}"${ded.artist_name ? ` by ${ded.artist_name}` : ''} on Christovibes Radio`;
    if (navigator.share) {
      navigator.share({ title: 'Christovibes Dedication', text, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text}\n${shareUrl}`).then(() => toast.success('Copied to clipboard!')).catch(() => {});
    }
  };

  return (
    <div data-testid="dedication-wall-page" className="pb-28 min-h-screen">
      {/* Header */}
      <div className="relative py-20 sm:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-[#0F1115] to-[#0F1115]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#D4AF37]/10 mb-5">
            <Heart size={30} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-white mb-4" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Dedication Wall
          </h1>
          <p className="text-base sm:text-lg text-[#94A3B8] max-w-xl mx-auto">
            Song dedications and shoutouts from our amazing listener community. Every request tells a story.
          </p>
          {total > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#94A3B8] bg-white/5 px-4 py-2 rounded-full">
              <MessageCircle size={14} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-semibold">{total}</span> dedications played on air
            </div>
          )}
        </div>
      </div>

      {/* Dedications Feed */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : dedications.length === 0 ? (
          <div className="text-center py-20 bg-[#171A21] rounded-2xl border border-white/5">
            <Heart size={48} className="mx-auto text-[#94A3B8]/30 mb-4" />
            <p className="text-[#94A3B8] text-lg">No dedications yet</p>
            <p className="text-[#94A3B8]/60 text-sm mt-2">Request a song on the home page with a message to see it here!</p>
          </div>
        ) : (
          <div className="space-y-5">
            {dedications.map((ded, i) => {
              const palette = ACCENT_PALETTES[i % ACCENT_PALETTES.length];
              return (
                <div
                  key={ded.id}
                  data-testid={`dedication-${i}`}
                  className={`bg-gradient-to-r ${palette.bg} rounded-2xl border ${palette.border} p-6 sm:p-8 animate-fade-in-up group hover:-translate-y-0.5 transition-transform duration-300`}
                  style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}
                >
                  {/* Quote */}
                  <div className="flex gap-3 mb-5">
                    <Quote size={24} className={`${palette.icon} shrink-0 mt-1 opacity-60`} />
                    <p className="text-white text-lg sm:text-xl leading-relaxed font-medium italic">
                      {ded.message}
                    </p>
                  </div>

                  {/* Song Info */}
                  <div className="flex items-center gap-3 mb-4 pl-9">
                    <div className={`w-10 h-10 rounded-xl bg-[#0F1115]/40 flex items-center justify-center shrink-0`}>
                      <Music size={16} className={palette.icon} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">{ded.song_name}</p>
                      {ded.artist_name && <p className={`text-sm ${palette.icon} opacity-80`}>{ded.artist_name}</p>}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pl-9">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#0F1115]/40 flex items-center justify-center">
                        <span className="text-xs font-semibold text-[#D4AF37]">{(ded.listener_name || 'A')[0].toUpperCase()}</span>
                      </div>
                      <span className="text-sm text-[#94A3B8]">{ded.listener_name}</span>
                      <span className="text-xs text-[#94A3B8]/50">&middot;</span>
                      <span className="text-xs text-[#94A3B8]/50">{timeAgo(ded.created_at)}</span>
                    </div>
                    <button
                      data-testid={`share-dedication-${i}`}
                      onClick={() => handleShare(ded)}
                      className="p-2 rounded-lg hover:bg-white/10 text-[#94A3B8] hover:text-white transition-all sm:opacity-0 sm:group-hover:opacity-100"
                      title="Share this dedication"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
